import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000';

// Get token from localStorage
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json'
  };
};

export interface SendSmsRequest {
  toNumber: string;
  message: string;
  clientId?: string;
  claimId?: string;
}

export interface SendTemplatedSmsRequest {
  toNumber: string;
  templateName: string;
  variables: Record<string, string>;
  clientId?: string;
  claimId?: string;
}

export interface SmsTemplate {
  name: string;
  description: string;
  variables: string[];
  preview: string;
}

export interface SmsConfig {
  enabled: boolean;
  fromNumber: string;
  configured: boolean;
}

export interface SendSmsResponse {
  success: boolean;
  message_sid?: string;
  status?: string;
  segments?: number;
  error?: string;
  error_code?: number;
}

export interface SmsMessageStatus {
  success: boolean;
  sid?: string;
  status?: string;
  to?: string;
  from?: string;
  body?: string;
  date_sent?: string;
  date_updated?: string;
  error_code?: number;
  error_message?: string;
  error?: string;
}

// Send SMS message
export const sendSms = async (smsData: SendSmsRequest): Promise<SendSmsResponse> => {
  try {
    const response = await axios.post<SendSmsResponse>(
      `${API_URL}/ui/sms/send`,
      {
        to_number: smsData.toNumber,
        message: smsData.message,
        client_id: smsData.clientId,
        claim_id: smsData.claimId
      },
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error: any) {
    throw error;
  }
};

// Send templated SMS
export const sendTemplatedSms = async (smsData: SendTemplatedSmsRequest): Promise<SendSmsResponse> => {
  try {
    const response = await axios.post<SendSmsResponse>(
      `${API_URL}/ui/sms/send-templated`,
      {
        to_number: smsData.toNumber,
        template_name: smsData.templateName,
        variables: smsData.variables,
        client_id: smsData.clientId,
        claim_id: smsData.claimId
      },
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error: any) {
    throw error;
  }
};

// Get SMS templates
export const getSmsTemplates = async (): Promise<{ templates: SmsTemplate[] }> => {
  try {
    const response = await axios.get<{ templates: SmsTemplate[] }>(
      `${API_URL}/ui/sms/templates`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error: any) {
    throw error;
  }
};

// Get SMS message status
export const getSmsStatus = async (messageSid: string): Promise<SmsMessageStatus> => {
  try {
    const response = await axios.get<SmsMessageStatus>(
      `${API_URL}/ui/sms/status/${messageSid}`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error: any) {
    throw error;
  }
};

// Get SMS configuration status
export const getSmsConfig = async (): Promise<SmsConfig> => {
  try {
    const response = await axios.get<SmsConfig>(
      `${API_URL}/ui/sms/config`,
      { headers: getAuthHeaders() }
    );
    return response.data;
  } catch (error: any) {
    throw error;
  }
};

// Format phone number to E.164 format (UK numbers)
export const formatPhoneNumber = (phone: string): string => {
  // Remove all non-digit characters
  let cleaned = phone.replace(/\D/g, '');

  // Handle UK numbers
  if (cleaned.startsWith('44')) {
    return '+' + cleaned;
  } else if (cleaned.startsWith('0')) {
    return '+44' + cleaned.substring(1);
  } else if (cleaned.length === 10) {
    // Assume UK mobile without leading 0
    return '+44' + cleaned;
  }

  // If already has +, return as is
  if (phone.startsWith('+')) {
    return phone;
  }

  return '+44' + cleaned;
};

// Validate phone number (basic UK validation)
export const isValidUKPhone = (phone: string): boolean => {
  const formatted = formatPhoneNumber(phone);
  // UK mobile numbers after +44 should be 10 digits starting with 7
  const ukMobileRegex = /^\+44[0-9]{10}$/;
  return ukMobileRegex.test(formatted);
};
